import { Transition } from '@headlessui/react';
import {
    Fragment,
    useCallback,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

const GLOBAL_NOTIFICATION_TTL_SECONDS = 45;

const NotificationContext = createContext({
    notification: null,
    push: () => {},
    dismiss: () => {},
});

export function useNotifications() {
    return useContext(NotificationContext);
}

export function NotificationContextProvider(props) {
    const [notification, setNotification] = useState(null);

    /**
     * Replaces the active notification.
     * @param {*} nextNotification - the notification to display
     */
    const handlePushNotification = useCallback((nextNotification) => {
        setNotification({
            ...nextNotification,
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        });
    }, []);

    /**
     * Removes the active notification.
     */
    const handleDismissNotification = useCallback(() => {
        setNotification(null);
    }, []);

    const contextValue = useMemo(
        () => ({
            notification,
            push: handlePushNotification,
            dismiss: handleDismissNotification,
        }),
        [notification, handlePushNotification, handleDismissNotification]
    );

    return (
        <NotificationContext.Provider value={contextValue}>
            {props.children}
        </NotificationContext.Provider>
    );
}

export function NotificationPanel({ className = '' }) {
    const { notification, dismiss } = useNotifications();

    if (!notification) return null;

    return (
        <div
            aria-live="assertive"
            className={`flex h-7 max-w-full items-center ${className}`}
        >
            <Notification
                key={notification.id}
                onClose={dismiss}
                {...notification}
            />
        </div>
    );
}

function Notification(props) {
    const { title, message, variant, ttl, error, onClose, useExactTtl } = props;

    const [progress, setProgress] = useState(0);
    const [cardWidth, setCardWidth] = useState(null);
    const textRef = useRef(null);
    const text = message ? `${title} - ${message}` : title;
    const parsedTtl = Number(ttl);
    const hasTimedNotification = Number.isFinite(parsedTtl) && parsedTtl > 0;
    const isErrorNotification = variant === 'error';
    const effectiveTtl = hasTimedNotification
        ? useExactTtl
            ? parsedTtl
            : GLOBAL_NOTIFICATION_TTL_SECONDS
        : null;
    const shouldShowGradientProgress = Boolean(
        effectiveTtl && !isErrorNotification
    );

    /**
     * If the notification has a ttl, start the interval
     */
    useEffect(() => {
        if (!effectiveTtl) return undefined;

        setProgress(0);
        const durationMs = effectiveTtl * 1000;
        if (isErrorNotification) {
            const timeoutId = window.setTimeout(() => {
                onClose();
            }, durationMs);
            return () => {
                window.clearTimeout(timeoutId);
            };
        }

        const startedAt = Date.now();
        const intervalId = window.setInterval(() => {
            const elapsedMs = Date.now() - startedAt;
            setProgress(Math.min((elapsedMs / durationMs) * 100, 100));
        }, 100);

        const timeoutId = window.setTimeout(() => {
            setProgress(100);
            onClose();
        }, durationMs);

        return () => {
            window.clearInterval(intervalId);
            window.clearTimeout(timeoutId);
        };
    }, [effectiveTtl, isErrorNotification, onClose]);

    /**
     * If an error is passed, log it to the console
     * - Useful for debugging and removes the need to push a notification
     *   and console.error for every error.
     */
    useEffect(() => {
        if (error) {
            console.error(error);
        }
    }, [error]);

    useEffect(() => {
        const textElement = textRef.current;
        if (!textElement) return undefined;

        const frameId = window.requestAnimationFrame(() => {
            const contentWidth = textElement.scrollWidth;
            setCardWidth(Math.max(contentWidth, 56));
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [text]);

    useEffect(() => {
        const textElement = textRef.current;
        if (!textElement) return;

        let frameId = null;
        let startTimeoutId = null;
        let endTimeoutId = null;
        let setupFrameId = null;
        let lastTimestamp = 0;
        const speedPxPerSecond = 38;
        const startPauseMs = 900;
        const endPauseMs = 700;

        const clearTimers = () => {
            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
                frameId = null;
            }
            if (startTimeoutId !== null) {
                window.clearTimeout(startTimeoutId);
                startTimeoutId = null;
            }
            if (endTimeoutId !== null) {
                window.clearTimeout(endTimeoutId);
                endTimeoutId = null;
            }
        };

        const animate = (timestamp, maxScroll) => {
            if (!textElement.isConnected) return;

            if (!lastTimestamp) {
                lastTimestamp = timestamp;
            }

            const elapsedSeconds = (timestamp - lastTimestamp) / 1000;
            lastTimestamp = timestamp;

            const next = Math.min(
                textElement.scrollLeft + speedPxPerSecond * elapsedSeconds,
                maxScroll
            );
            textElement.scrollLeft = next;

            if (next >= maxScroll) {
                lastTimestamp = 0;
                endTimeoutId = window.setTimeout(() => {
                    textElement.scrollLeft = 0;
                    startTimeoutId = window.setTimeout(() => {
                        frameId = window.requestAnimationFrame((ts) =>
                            animate(ts, maxScroll)
                        );
                    }, startPauseMs);
                }, endPauseMs);
                return;
            }

            frameId = window.requestAnimationFrame((ts) =>
                animate(ts, maxScroll)
            );
        };

        const start = () => {
            const maxScroll = Math.max(
                textElement.scrollWidth - textElement.clientWidth,
                0
            );

            if (maxScroll <= 0) {
                textElement.scrollLeft = 0;
                return;
            }

            textElement.scrollLeft = 0;
            lastTimestamp = 0;
            startTimeoutId = window.setTimeout(() => {
                frameId = window.requestAnimationFrame((ts) =>
                    animate(ts, maxScroll)
                );
            }, startPauseMs);
        };

        setupFrameId = window.requestAnimationFrame(start);

        return () => {
            if (setupFrameId !== null) {
                window.cancelAnimationFrame(setupFrameId);
            }
            clearTimers();
        };
    }, [text]);

    return (
        <Transition
            show
            as={Fragment}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <div
                className={`pointer-events-auto relative z-[100] h-7 max-w-full overflow-hidden rounded-full ${
                    isErrorNotification ? 'bg-red-600' : 'bg-white/20'
                }`}
                style={{
                    width: cardWidth ? `${cardWidth}px` : undefined,
                }}
            >
                {shouldShowGradientProgress && (
                    <div
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary-700 to-secondary-700"
                        style={{
                            width: `${progress}%`,
                            transition: 'width 100ms linear',
                        }}
                    ></div>
                )}
                <div
                    ref={textRef}
                    className="relative z-10 h-full w-full overflow-x-auto whitespace-nowrap px-3 text-xs font-medium leading-7 text-white [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                    {text}
                </div>
            </div>
        </Transition>
    );
}
